import api from './index';
import { ApiResponse, AccessCode, SiteConfig, SiteStatus } from '../types';

// Helper to add admin password header
const adminHeaders = (password: string) => ({
  headers: { 'X-Admin-Password': password },
});

export const adminApi = {
  /**
   * Verify admin password
   */
  async login(password: string): Promise<boolean> {
    const response = await api.post<ApiResponse>('/admin/login', { password });
    return response.data.success;
  },

  /**
   * Get all access codes
   */
  async getCodes(password: string): Promise<AccessCode[]> {
    const response = await api.get<ApiResponse<{ codes: AccessCode[] }>>(
      '/admin/codes',
      adminHeaders(password)
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to get codes');
    }

    return response.data.data.codes;
  },

  /**
   * Create a new access code
   * @param password Admin password
   * @param options Code options - email (optional), customTitle (optional), collectionHandle (optional)
   */
  async createCode(
    password: string,
    options: { email?: string; customTitle?: string; collectionHandle?: string } | string
  ): Promise<AccessCode> {
    // Support legacy call with just email string
    const body = typeof options === 'string' ? { email: options } : options;

    const response = await api.post<ApiResponse<{ accessCode: AccessCode }>>(
      '/admin/codes',
      body,
      adminHeaders(password)
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to create code');
    }

    return response.data.data.accessCode;
  },

  /**
   * Create multiple access codes
   */
  async createBulkCodes(
    password: string,
    emails: string[]
  ): Promise<{ success: AccessCode[]; errors: { email: string; error: string }[] }> {
    const response = await api.post<
      ApiResponse<{ success: AccessCode[]; errors: { email: string; error: string }[] }>
    >('/admin/codes/bulk', { emails }, adminHeaders(password));

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to create codes');
    }

    return response.data.data;
  },

  /**
   * Delete an access code
   */
  async deleteCode(password: string, id: number): Promise<void> {
    const response = await api.delete<ApiResponse>(
      `/admin/codes/${id}`,
      adminHeaders(password)
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to delete code');
    }
  },

  /**
   * Toggle access code active status
   */
  async toggleCode(password: string, id: number): Promise<AccessCode> {
    const response = await api.patch<ApiResponse<{ accessCode: AccessCode }>>(
      `/admin/codes/${id}`,
      {},
      adminHeaders(password)
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to toggle code');
    }

    return response.data.data.accessCode;
  },

  /**
   * Get site configuration
   */
  async getConfig(password: string): Promise<{ config: SiteConfig; status: SiteStatus }> {
    const response = await api.get<ApiResponse<{ config: SiteConfig; status: SiteStatus }>>(
      '/admin/config',
      adminHeaders(password)
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to get config');
    }

    return response.data.data;
  },

  /**
   * Start countdown timer
   */
  async startCountdown(password: string, durationMinutes: number): Promise<SiteConfig> {
    const response = await api.post<ApiResponse<{ config: SiteConfig }>>(
      '/admin/config/start',
      { durationMinutes },
      adminHeaders(password)
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to start countdown');
    }

    return response.data.data.config;
  },

  /**
   * Stop countdown timer
   */
  async stopCountdown(password: string, clearSessions = false): Promise<SiteConfig> {
    const response = await api.post<ApiResponse<{ config: SiteConfig }>>(
      '/admin/config/stop',
      { clearSessions },
      adminHeaders(password)
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to stop countdown');
    }

    return response.data.data.config;
  },
};

export default adminApi;
